import { cardMask } from './card';
import type { MembershipFormatters } from './format';
import { isRecord } from './guards';
import type { MembershipLine } from './lines';
import { canUpdateCard, nextChargePriceMinor } from './manage';
import type { CardFormReply, MembershipPaymentAction } from './purchase';
import type { Membership, MembershipStatus } from './types';
import type { LoadState } from './view';

/** Status reported by `POST /memberships/me/card` and `/me/card/confirm`. */
export type MembershipCardChangeStatus =
  | 'updated'
  | 'action_required'
  | 'pending';

/** Answer of a card change or of its confirmation. */
export interface MembershipCardChangeResult {
  status: MembershipCardChangeStatus;
  /** Token the confirmation must name; always present with `action_required`. */
  tokenId: string | null;
  /** Present only with `action_required`. */
  action: MembershipPaymentAction | null;
  membership: Membership | null;
}

/** Body of `POST /memberships/me/card`; the payment fields come from the card form and are sent as is. */
export interface MembershipCardChangeInput {
  paymentMethod: Record<string, unknown>;
  browserInfo: Record<string, unknown> | null;
  origin: string;
  /** Where the bank sends the customer back after a redirect 3DS. */
  returnUrl: string;
  /** Courier zone of the current address; absent when unknown, then the backend takes the plan's first zone. */
  zoneId?: string;
}

/** The form shows the action of `action_required`; every other status ends its part. */
export const toCardChangeFormReply = (
  result: MembershipCardChangeResult,
): CardFormReply =>
  result.status === 'action_required' && result.action
    ? { kind: 'action', action: result.action }
    : { kind: 'done' };

/** Saved before a redirect 3DS of a card change and read back when the bank returns. */
export interface MembershipCardRedirect {
  tokenId: string;
  planCode: string;
  /** The membership was overdue, so the new card is charged at once. */
  wasPastDue: boolean;
}

/** Guards a card redirect marker read back from storage. */
export const isCardRedirect = (
  value: unknown,
): value is MembershipCardRedirect =>
  isRecord(value) &&
  typeof value.tokenId === 'string' &&
  value.tokenId !== '' &&
  typeof value.planCode === 'string' &&
  typeof value.wasPastDue === 'boolean';

/**
 * Why the "Add card" sheet (`?modal=club-card`) could not save the card.
 * `payment_rejected`: the payment service refused the request, nothing was charged.
 * `redirect_lost`: the bank sent the customer back, but the marker of the
 * change is gone (expired, another browser), so the card cannot be confirmed.
 */
export type MembershipCardChangeFailure =
  | 'card_refused'
  | 'payment_rejected'
  | 'not_confirmed'
  | 'redirect_lost'
  | 'form_unavailable';

/** Where the "Add card" sheet (`?modal=club-card`) sends the customer once the card is settled. */
export type MembershipCardChangeExit = 'updated' | 'restoring';

const CARD_FAILURE_KEYS: Record<MembershipCardChangeFailure, string> = {
  card_refused: 'membership:purchaseDeclined',
  payment_rejected: 'membership:paymentNothingCharged',
  not_confirmed: 'membership:cardNotConfirmed',
  redirect_lost: 'membership:cardNotConfirmedAddAgain',
  form_unavailable: 'membership:purchaseUnavailable',
};

/** Message above the card form. */
export const cardChangeFailureLine = (
  failure: MembershipCardChangeFailure,
): MembershipLine => ({ key: CARD_FAILURE_KEYS[failure], tone: 'warning' });

/** Line above the card form: an overdue membership is charged at once. */
export const cardChangeIntroLine = (
  membership: Membership,
  f: MembershipFormatters,
): MembershipLine =>
  membership.status === 'past_due'
    ? {
        key: 'membership:cardChargeNow',
        params: {
          price: f.money(
            nextChargePriceMinor(membership),
            membership.plan.currency,
          ),
        },
      }
    : { key: 'membership:cardNextPayment' };

/** "Current card: Visa · 4242" above the card form; `null` (hidden) when the card is unknown. */
export const currentCardLine = (
  membership: Membership,
): MembershipLine | null => {
  const card = cardMask(membership.card);
  return card ? { key: 'membership:currentCard', params: { card } } : null;
};

/** Screen state of the "Add card" sheet (`?modal=club-card`). */
export type MembershipCardChangeView =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'unavailable' }
  | { kind: 'restoring' }
  | { kind: 'ready'; membership: Membership };

/** Operations of the membership that keep the card form closed: one operation at a time. */
export interface MembershipCardChangeActivity {
  /** The membership page waits for an overdue membership to be restored after a card change. */
  isRestoring: boolean;
  /** A cancel, plan switch or "Keep current plan" is in flight. */
  isManaging: boolean;
}

/**
 * The "Add card" sheet (`?modal=club-card`) serves a membership the customer bought while it renews or
 * is overdue, and only while no other operation runs: during a restoration
 * wait the overdue membership would be charged a second time, so the page
 * hands over to the membership page; during a manage request it waits. It
 * depends on `/me` alone — the zone and its plans play no part, so a
 * customer without a membership is told so at once.
 */
export const toCardChangeView = (
  me: LoadState<Membership | null>,
  activity: MembershipCardChangeActivity,
): MembershipCardChangeView => {
  if (me.status === 'failed') {
    return { kind: 'error' };
  }
  if (me.status !== 'ready') {
    return { kind: 'loading' };
  }
  if (me.data === null) {
    return { kind: 'unavailable' };
  }
  if (activity.isRestoring) {
    return { kind: 'restoring' };
  }
  if (activity.isManaging) {
    return { kind: 'loading' };
  }
  return canUpdateCard(me.data)
    ? { kind: 'ready', membership: me.data }
    : { kind: 'unavailable' };
};

/** Payload of `Membership: Card Update Viewed`. */
export interface MembershipCardUpdateViewedPayload {
  planCode: string;
  status: MembershipStatus;
}

/** Payload of `Membership: Card Updated`. */
export interface MembershipCardUpdatedPayload {
  planCode: string;
  wasPastDue: boolean;
  /** `pending`: the card is saved, the overdue charge is still running. */
  status: 'updated' | 'pending';
}

/** Payload of `Membership: Card Update Failed`. */
export interface MembershipCardUpdateFailedPayload {
  /** Code of the member's plan; `null` when unknown — a bank return without its marker. */
  planCode: string | null;
  reason: MembershipCardChangeFailure;
}

/** Events of the "Add card" sheet (`?modal=club-card`); mapped to analytics by the composition root. */
export type MembershipCardChangeEvent =
  | { name: 'cardUpdated'; payload: MembershipCardUpdatedPayload }
  | { name: 'cardUpdateFailed'; payload: MembershipCardUpdateFailedPayload };
